import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";

import {
    deleteDocument,
    fetchPersonalDocumentTypeslist,
    uploadDocuments,
    certificatesAndDocumentApprove,
} from "../../service";
import { commonSwal } from "../../../../components/swalAlert/swalAlert";
import DivSpinner from "../../../../components/divSpinner";
import ModalTemplate from "../../../../components/modal/modalTemplate";
import styles from "../styles";
import { formatTimestampInMap } from "../../../../utils";
import SetExpiryDate from "../SetExpiryDate";
import { theme } from "../../../../theme";
import NotificationMessages from "../NotificationMessages";
import CheckLists from "../CheckLists";
import FilesView from "../filesView/FilesView";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];

const PersonalDocumentsFileAndUploader = ({
    ftm_id,
    show,
    handleClose,
    item,
    items,
    parentRefresh,
    handleNextItem,
    handlePrevItem,
}) => {
    const index = useMemo(
        () => items.findIndex((t) => t.personal_document_type_id === item?.personal_document_type_id),
        [items, item?.personal_document_type_id]
    );

    const title = `Upload ${item?.personal_document_type_name} documents (${index + 1} of ${items.length})`;

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [personalDocumentId, setPersonalDocumentId] = useState(null);
    const [isApproveDocument, setIsApproveDocument] = useState(false);
    const [approveDocumentDetails, setApproveDocumentDetails] = useState(null);
    const [checkLists, setCheckLists] = useState(null);
    const [acceptCheckList, setAcceptCheckList] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileViewIndex, setFileViewIndex] = useState(0);

    const expiryRef = useRef();

    const handleFileViewOpen = (index) => {
        setFileViewIndex(index);
        setShowFileViewModal(true);
    }

    const handleFileViewClose = () => {
        setShowFileViewModal(false);
    }

    const nav = useMemo(
        () => ({
            prev: index > 0 ? items[index - 1] : null,
            next: index < items.length - 1 ? items[index + 1] : null,
        }),
        [index, items]
    );

    /** ---------- Fetch existing docs ---------- */
    useEffect(() => {
        const fetchDocs = async () => {
            setLoading(true);
            try {
                const response = await fetchPersonalDocumentTypeslist(
                    ftm_id,
                    item?.personal_document_type_id,
                    'current'
                );

                if (response?.status === "Success") {
                    const docs = response?.documents?.ftm_personal_document_files || [];
                    const isApprove = response?.documents?.ftm_personal_document_is_approve === 1;
                    const checkList = response?.documents?.personal_document_type?.personal_document_type_check_list;

                    const mappedFiles = docs.map((doc, i) => {
                        const fileName = doc.ftm_personal_document_file_path.split("/").pop();
                        const isImage = IMAGE_EXTENSIONS.some((ext) =>
                            fileName.toLowerCase().endsWith(ext)
                        );

                        return {
                            id: `${Date.now()}-${i}`,
                            preview: doc.ftm_personal_document_file_path,
                            fileName,
                            isImage,
                            status: "success",
                            ftm_personal_document_file_id: doc.ftm_personal_document_file_id,
                            type: "EXISTING",
                            date_time_uploaded: doc.ftm_personal_document_file_uploaded_at
                        };
                    });

                    setFiles(mappedFiles);
                    setPersonalDocumentId(response?.documents?.ftm_personal_document_id);
                    setIsApproveDocument(isApprove);
                    setCheckLists(checkList);
                    setApproveDocumentDetails(response?.documents?.document_approve);
                } else {
                    setFiles([]);
                    setIsApproveDocument(false);
                    setCheckLists(null);
                    setApproveDocumentDetails(null);
                }
            } catch (err) {
                console.error("Error fetching documents:", err);
                setFiles([]);
            } finally {
                setLoading(false);
                setAcceptCheckList(false);
            }
        };

        fetchDocs();
    }, [ftm_id, item?.personal_document_type_id, refresh]);

    const hasFiles = files.length > 0;
    const hasUploadingFiles = files.some(
        (f) => f.status !== "success" && f.status !== "error"
    );

    const updateFileStatus = useCallback((id, updates) => {
        setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
    }, []);

    const uploadFile = useCallback(
        async (fileObj) => {
            updateFileStatus(fileObj.id, { status: "uploading" });
            try {
                const response = await uploadDocuments({
                    ftm_id,
                    ftm_document_type_id: 0,
                    ftm_document_type: item?.personal_document_type_code,
                    document_type: 0,
                    ftm_document: fileObj.file,
                });

                if (response?.status === "Success") {
                    updateFileStatus(fileObj.id, {
                        status: "success",
                        ftm_personal_document_file_id: response.ftm_personal_document_file_id,
                        document_path: response.document_path,
                    });
                    setPersonalDocumentId(response.ftm_personal_document_id);
                    parentRefresh();
                } else {
                    updateFileStatus(fileObj.id, { status: "error" });
                }
            } catch {
                updateFileStatus(fileObj.id, { status: "error" });
            }
        },
        [ftm_id, item?.personal_document_type_code, updateFileStatus, parentRefresh]
    );

    /** ---------- Drag & drop ---------- */
    const onDrop = useCallback(
        async (acceptedFiles) => {
            const newFiles = acceptedFiles.map((file, i) => {
                const fileName = file.name;
                const isImage = IMAGE_EXTENSIONS.some((ext) =>
                    fileName.toLowerCase().endsWith(ext)
                );

                const fileObj = {
                    id: `${Date.now()}-${i}`,
                    file,
                    preview: URL.createObjectURL(file),
                    fileName,
                    isImage,
                    status: "pending",
                    type: "NEW",
                    date_time_uploaded: new Date().getTime(),
                };

                // start upload immediately
                // uploadFile(fileObj);
                return fileObj;
            });

            setFiles((prev) => [...newFiles, ...prev]);

            setIsUploadingFiles(true);
            for (const obj of newFiles) {
                await uploadFile(obj);
                if(obj.id === newFiles[newFiles.length -1].id){
                    setIsUploadingFiles(false);
                }
            }
        },
        [uploadFile] // ✅ include uploadFile to avoid stale closure
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [], "application/pdf": [] },
        multiple: true,
    });

    /** Cleanup previews on unmount or when files change */
    useEffect(() => {
        return () => {
            files.forEach((f) => {
                if (f.preview?.startsWith("blob:")) URL.revokeObjectURL(f.preview);
            });
        };
    }, [files]);

    const revokePreview = (id) => {
        const file = files.find((f) => f.id === id);
        if (file?.preview?.startsWith("blob:")) URL.revokeObjectURL(file.preview);
    };

    const removeFile = (file) => {
        const { id, status, ftm_personal_document_file_id } = file;

        if (status === "error") {
            revokePreview(id);
            setFiles((prev) => prev.filter((f) => f.id !== id));
            return;
        }

        commonSwal({
            notificationType: "UPLOAD_IMAGE_DELETE",
            preConfirm: () => deleteDocument(ftm_personal_document_file_id),
            isConfirmed: (result) => {
                if (result?.isConfirmed && result?.value?.status === "Success") {
                    revokePreview(id);
                    setFiles((prev) => prev.filter((f) => f.id !== id));
                } else {
                    updateFileStatus(id, { status: "success" });
                }
            },
        });
    };

    const documentApprove = async () => {
        try {
            commonSwal({
                notificationType: "APPROVE_FTM_DOCUMENT",
                preConfirm: async () => {
                    const saved = await expiryRef.current?.saveExpiryData();
                    if (saved === false) return;

                    return await certificatesAndDocumentApprove({
                        document_id: personalDocumentId,
                        document_type: "personal",
                    })
                },
                isConfirmed: (result) => {
                    if (result.isConfirmed) {
                        const { status, message } = result?.value || {};
                        if (status === "Success") {
                            setIsApproveDocument(1);
                            parentRefresh();
                            setRefresh(refresh+1);
                            toast.success(message);
                        }
                    }
                },
            });
        } catch (err) {
            console.error("Approve error:", err);
        }
    };

    const goNext = (id) => !hasUploadingFiles && handleNextItem(id);
    const goPrev = (id) => !hasUploadingFiles && handlePrevItem(id);

    return (
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            centered
            title={title}
            backdrop="static"
            size={"lg"}
            dialogClassName = {"modal-900w"}
            body={
                <section>
                    <div className="row">
                        {loading ? (
                            <div style={{ height: `${window.innerHeight - 387}px` }}>
                                <DivSpinner />
                            </div>
                        ) : (
                            <>
                                <div className="col-7">
                                    {isApproveDocument && (
                                        <NotificationMessages
                                            color={theme.main}
                                            override={`${item?.personal_document_type_name} document approved`}
                                        />
                                    )}

                                    {/* Dropzone */}
                                    {!isApproveDocument && (
                                        <div {...getRootProps({ style: styles.dropzone })}>
                                            <input {...getInputProps()} />
                                            <p className="font-weight-bold mt-3">
                                                {isDragActive
                                                    ? "Drop the files here..."
                                                    : "Drag & drop files or click to select"}
                                            </p>
                                        </div>
                                    )}

                                    {/* Files */}
                                    {hasFiles ? (
                                        <div style={{ overflow: "auto", maxHeight: `${window.innerHeight - 387}px` }}>
                                            <div
                                                style={{
                                                    ...styles.thumbsContainer,
                                                    justifyContent: window.innerWidth < 770 ? "center" : ""
                                                }}
                                            >
                                                {files.map((file) => (
                                                    <FileItem key={file.id} file={file} removeFile={removeFile} handleFileViewOpen={handleFileViewOpen}/>
                                                ))}

                                                <FilesView 
                                                    show={showFileViewModal} 
                                                    handleClose={handleFileViewClose} 
                                                    title={item?.personal_document_type_name + " Files"} 
                                                    files={files} 
                                                    selectedId={fileViewIndex} 
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={styles.emptyState}>
                                            <i className="material-icons" style={{ fontSize: "64px", color: "#ccc" }}>
                                                folder_open
                                            </i>
                                            <p style={{ fontSize: "14px", color: "#777" }}>No files uploaded yet</p>
                                        </div>
                                    )}
                                </div>
                                <div className="col-5">
                                    {checkLists && (
                                        <CheckLists 
                                            list={checkLists} 
                                            accepted={acceptCheckList} 
                                            handleAccepted={setAcceptCheckList} 
                                            isApprove={isApproveDocument} 
                                            approveDocumentDetails={approveDocumentDetails}
                                            type="personalDocument"
                                        />
                                    )}

                                    <div className={`mt-${checkLists ? '4' : '1'}`}>
                                        <SetExpiryDate 
                                            ref={expiryRef} 
                                            document_id={personalDocumentId} 
                                            document_type="personal" 
                                            document_types_is_expiry_date_mandatory={item?.personal_document_type_is_expiry_date_mandatory}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            }
            footer={
                <div className="d-flex w-100">
                    <div className="d-flex">
                        {nav.prev && (
                            <button 
                                type="button" 
                                className="btn btn-link btn-sm text-dark d-flex align-items-center ms-2 me-2" 
                                onClick={() => goPrev(item?.personal_document_type_id)}
                            >
                                <i className="material-icons me-1" style={{fontSize: '15px'}}>arrow_back</i>
                                <span>Back</span>
                            </button>
                        )}

                        {nav.next ? (
                            <button 
                                type="button" 
                                className="btn btn-link btn-sm text-dark d-flex align-items-center ms-2"
                                onClick={() => goNext(item?.personal_document_type_id)}
                            >
                                <span>Next</span>
                                <i className="material-icons me-1" style={{fontSize: '15px'}}>arrow_forward</i>
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-dark d-flex align-items-center ms-2"
                                onClick={handleClose}
                            >
                                <span>Finish</span>
                                <i className="material-icons ms-1" style={{ fontSize: "15px" }}>done_all</i>
                            </button>
                        )}
                    </div>

                    <div className="ms-auto">
                        <button
                            className={`btn btn-${theme.main} btn-sm`}
                            onClick={documentApprove}
                            disabled={
                                isApproveDocument || (checkLists && !acceptCheckList) || isUploadingFiles
                            }
                        >
                            <i className="material-icons me-1" style={{fontSize: '15px'}}>check</i>
                            Approve
                        </button>
                    </div>
                </div>
            }
        />
    );
};

const FileItem = ({ file, removeFile, handleFileViewOpen }) => {
    const statusIcons = {
        uploading: { icon: "upload", text: "UPLOADING...", class: "text-warning" },
        success: { icon: "verified", text: "UPLOADED", class: "text-success" },
        error: { icon: "error", text: "ERROR", class: "text-danger" },
        existing: { icon: "verified", text: "UPLOADED", class: "text-success" },
    };

    const status =
        file.type === "NEW"
            ? statusIcons[file.status] || statusIcons.uploading
            : statusIcons.existing;

    return (
        <div style={styles.thumb} className="position-relative" onClick={() => handleFileViewOpen(file.id)}>
            {(file.status === "success" || file.status === "error") && (
                <button
                    onClick={() => removeFile(file)}
                    style={styles.topRightRemoveBtn}
                    title="Remove"
                >
                    &times;
                </button>
            )}

            {file.isImage ? (
                <img src={file.preview} alt={file.fileName} style={styles.thumbImage} />
            ) : (
                <div style={styles.pdfPreview}>
                    <i className="material-icons" style={{ fontSize: "48px" }}>
                        file_present
                    </i>
                </div>
            )}

            <p className="mb-1 font-weight-bold text-dark" style={styles.fileName}>
                {file?.date_time_uploaded ? formatTimestampInMap(file.date_time_uploaded, true) : "N/A"}
            </p>

            <div className="mt-1" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <i className={`material-icons ${status.class} text-md`}>{status.icon}</i>
                <span className="font-weight-bold text-dark text-xs">{status.text}</span>
            </div>
        </div>
    );
};

export default PersonalDocumentsFileAndUploader;
